// Fixed monthly commitments — list + inline add + monthly pay flow.
import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { refetchCommitments } from "@/lib/useCommitmentsCache";
import { useI18n } from "@/context/I18nContext";
import { InstallmentPayAccountModal } from "@/components/installments/InstallmentPayAccountModal";
import { StatusPill } from "@/components/dashboard/StatusPill";

export function CommitmentsSection({ items, ccy }) {
  const { t, lang } = useI18n();
  const AR = lang === "ar";
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [day, setDay] = useState("1");
  const [busy, setBusy] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [paying, setPaying] = useState(false);

  const add = async () => {
    if (!name.trim() || !Number(amount)) return;
    setBusy(true);
    try {
      await api.post("/commitments", { name: name.trim(), amount: Number(amount), currency: ccy, day_of_month: Math.min(28, Math.max(1, Number(day) || 1)) });
      setName(""); setAmount(""); setDay("1");
      refetchCommitments();
      toast.success(t("saved"));
    } catch { toast.error(t("error")); } finally { setBusy(false); }
  };

  const del = async (id) => { try { await api.delete(`/commitments/${id}`); refetchCommitments(); } catch { toast.error(t("error")); } };

  const doPay = async (accountId) => {
    if (!payTarget) return;
    setPaying(true);
    try {
      await api.post(`/commitments/${payTarget.id}/pay`, { account_id: accountId });
      refetchCommitments();
      toast.success(AR ? "تم تسجيل الدفعة ✓" : "Payment recorded ✓");
      setPayTarget(null);
    } catch { toast.error(t("error")); } finally { setPaying(false); }
  };

  return (
    <section className="space-y-2 mb-4">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">🟢 {t("fixedCommitments")}</p>
      {items.length === 0 && <p className="text-xs text-slate-400 py-1">—</p>}
      {items.map((c) => {
        const paid = c.status === "paid";
        return (
          <div key={c.id} className="p-2.5 rounded-xl neu-sm flex items-center gap-2" data-testid={`commitment-row-${c.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate">{c.name}</p>
              <p className="text-[10px] text-slate-500">{Number(c.amount).toFixed(0)} {c.currency} · {AR ? "يوم" : "day"} {c.day_of_month}</p>
            </div>
            <StatusPill status={c.status || "upcoming"} lang={lang} testId={`commitment-status-${c.id}`} />
            {!paid && (
              <button data-testid={`commitment-pay-${c.id}`} onClick={() => setPayTarget(c)} className="px-2 py-1.5 rounded-lg neu-fill-blue text-white text-[10px] font-bold active:scale-95">{AR ? "دفع" : "Pay"}</button>
            )}
            <button data-testid={`commitment-delete-${c.id}`} onClick={() => del(c.id)} className="w-7 h-7 rounded-lg grid place-items-center text-warn active:scale-95"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        );
      })}
      <div className="flex gap-1.5 pt-1">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("commitmentNamePlaceholder")}
          data-testid="commitment-name-input" className="flex-1 px-2.5 py-2 rounded-xl border border-border bg-card text-sm min-w-0" />
        <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder="0"
          data-testid="commitment-amount-input" className="w-16 px-2 py-2 rounded-xl border border-border bg-card text-sm" />
        <input value={day} onChange={(e) => setDay(e.target.value)} type="number" min="1" max="28" placeholder={AR ? "يوم" : "day"}
          data-testid="commitment-day-input" className="w-12 px-2 py-2 rounded-xl border border-border bg-card text-sm" />
        <button onClick={add} disabled={busy} data-testid="commitment-add-btn"
          className="w-10 h-10 rounded-xl grid place-items-center bg-emerald-500 text-white active:scale-95 disabled:opacity-50">
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <InstallmentPayAccountModal open={!!payTarget} amount={payTarget?.amount || 0} busy={paying}
        onConfirm={doPay} onClose={() => setPayTarget(null)} />
    </section>
  );
}
